// src/components/EventModal.js - TWO SECTION MODAL
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/components/EventModal.css';

const EventModal = ({ isOpen, onClose, event }) => {
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px'; // Prevent layout shift
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const onSubmit = async (data) => {
    if (!event || !event.id) {
      toast.error('Event information is missing');
      return;
    }

    try {
      setSubmitting(true);

      const registrationData = {
        event_id: event.id,
        full_name: data.fullName,
        email: data.email,
        phone: data.phone,
        organization: data.organization,
        additional_info: data.message
      };

      const response = await api.registerForEvent(registrationData);

      if (response.success) {
        toast.success(response.message || 'Registration successful! Check your email for confirmation.');
        reset();
        onClose();
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      {isOpen && event && (
        <div className="modal-wrapper">
          <motion.div
            className="event-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <div className="modal-center-wrapper">
            <motion.div
              className="event-modal-large"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <button className="event-modal-close" onClick={onClose}>×</button>

              <div className="event-modal-content">
                {/* SECTION 1: EVENT DETAILS */}
                <div className="event-details-section">
                  <div className="event-modal-image">
                    <SafeImage
                      src={event.image}
                      alt={event.title}
                      className="event-image"
                      width={400}
                      height={250}
                      placeholderText="Event"
                    />
                  </div>

                  <div className="event-info">
                    <div className="event-badges-modal">
                      <span className="event-category-badge-modal">{event.category_name}</span>
                      <span className="event-price-badge-modal">
                        {event.price === 'Free' ? 'Free' : event.price}
                      </span>
                    </div>

                    <h2 className="event-modal-title">{event.title}</h2>

                    <p className="event-modal-description">{event.description}</p>

                    <div className="event-meta-modal">
                      <div className="event-meta-item">
                        <i className="fas fa-calendar-alt"></i>
                        <div>
                          <strong>Date</strong>
                          <p>{formatDate(event.date)}</p>
                        </div>
                      </div>

                      <div className="event-meta-item">
                        <i className="fas fa-clock"></i>
                        <div>
                          <strong>Time</strong>
                          <p>{event.time}</p>
                        </div>
                      </div>

                      <div className="event-meta-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <div>
                          <strong>Location</strong>
                          <p>{event.location}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile scroll indicator */}
                  <div className="mobile-scroll-indicator">
                    <i className="fas fa-chevron-down"></i>
                    <span>Scroll down to register</span>
                  </div>
                </div>

                {/* SECTION 2: REGISTRATION FORM */}
                <div className="registration-form-section">
                  <h3 className="form-section-title">Register for this Event</h3>
                  <p className="form-section-subtitle">Fill in your details to secure your spot</p>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        placeholder="Enter your full name"
                        disabled={submitting}
                        {...register('fullName', { required: 'Name is required' })}
                      />
                      {errors.fullName && <span className="error">{errors.fullName.message}</span>}
                    </div>

                    <div className="form-group">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        disabled={submitting}
                        {...register('email', {
                          required: 'Email is required',
                          pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' }
                        })}
                      />
                      {errors.email && <span className="error">{errors.email.message}</span>}
                    </div>

                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        placeholder="+1 (246) 123-4567"
                        disabled={submitting}
                        {...register('phone', { required: 'Phone is required' })}
                      />
                      {errors.phone && <span className="error">{errors.phone.message}</span>}
                    </div>

                    <div className="form-group">
                      <label>Organization (Optional)</label>
                      <input
                        type="text"
                        placeholder="Your company or organization"
                        disabled={submitting}
                        {...register('organization')}
                      />
                    </div>

                    <div className="form-group">
                      <label>Additional Information (Optional)</label>
                      <textarea
                        placeholder="Any questions or special requirements?"
                        rows="3"
                        disabled={submitting}
                        {...register('message')}
                      ></textarea>
                    </div>

                    <button type="submit" className="submit-btn" disabled={submitting}>
                      {submitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Registering...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle"></i>
                          Complete Registration
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EventModal;